import React, { useEffect, useState, lazy, Suspense, useCallback } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import HomePage from "../../pages/home/home";
import Onboarding from "../../pages/onboarding/onboarding";
import LoginPage from "../../pages/login/login";
import { Category, TLocationState } from "../../types/types";
import useMediaQuery from "../../hooks/useMediaQuery";
import MainContentLayout from "../mainContentLayout/mainContentLayout";
import TreatmentPage from "../../pages/treatment/treatmentPage";
import ChatPage from "../../pages/chat/ChatPage";
import ProtectedRoute from "../protectedRoute/ProtectedRoute";
import { subCategories } from "../../utils/categories";
import { customerService, mobileWidth } from "../../utils/constants";
import { useAppSelector } from "../../services/hooks";
import { useActions } from "../../services/hooks/useActions";
import { isAuthSelector } from "../../services/selectors/auth.selectors";
import { getCookie } from "../../utils/token";
import Modal from "../modals/modal/Modal";
import InactivityWarning from "../modals/inactivityWarning/inactivityWarning";
import {
  useLazyGetAppointmentsByUserIdQuery,
  useGetUserQuery,
  useLazyGetContactDetailsQuery,
} from "../../services/api/user.api";
import Loader from "../loader/loader";
import {
  userAppointmentsSelector,
  userSelector,
  activeContactTokenSelector,
} from "../../services/selectors/user.selectors";
import { useLazyInitializeConnectionQuery, useDisconnectConnectionMutation } from "../../services/api/chat.api";
import { useAppSelector as useTypedSelector } from "../../services/hooks";
import { chatConnectionSelector } from "../../services/selectors/chat.selector";
import { useSelector } from "react-redux";
import { chatStateSelector } from "../../services/selectors/chat.selector";
import VideoCallWindow from "../modals/videoCallWindow/videoCallWindow";
import useInactivityTimer from "../../hooks/useInactivityTimer";
import InactivityLogoutPopup from "../modals/inactivityLogoutPopup/inactivityLogoutPopup";
import PublicOnlyRoute from "../publicOnlyRoute/PublicOnlyRoute";
import ConsentResponsePage from "../../pages/consentResponse/ConsentResponsePage";
// import KnowVentureChat from "../prevencherChat/KnowVentureChat";
// import i18n from "../../i18n";
// import ChangeEditingModeButton from "../changeEditingModeButton/changeEditingModeButton";
// import EditModeNotice from "../EditModeNotice/EditModeNotice";
import {
  // addKeysToAllTranslatedElements,
  enableTranslationMode,
} from "../../translationMode";
//import { isSuperAdmin } from "../../services/selectors/admin.selectors";
import PrivacyPolicyConsentPopup from "../modals/privacyPolicyConsentPopup/privacyPolicyConsentPopup";
import HotlineQuestionnairePopup from "../modals/hotlineQuestionnairePopup/hotlineQuestionnairePopup";
import { useGetMyHotlineQuestionnaireQuery } from "../../services/api/user.api";
// import { useDispatch } from "react-redux";
// import { setSuperAdmin } from "../../services/slices/admin.slice";
import { LanguageSelection } from "../LanguageSelection/LanguageSelection";
import { SelectPatientPage } from "../../pages/selectPatient/selectPatient";
window.enableTranslationMode = enableTranslationMode;


const LoginSmsCode = lazy(() => import("../../pages/login/loginSmsCode"));
const SigninPage = lazy(() => import("../../pages/signin/signin"));
const SigninPrivatePage = lazy(() => import("../../pages/signin/signin_private"));
const RestorePasswordPage = lazy(
  () => import("../../pages/restore_password/restorePasswordPage")
);
const PocGuestsChatPage = lazy(
  () => import("../../pages/prevencher/PocGuestsChatPage")
);
const Terms = lazy(() => import("../terms/terms"));
const ForumPage = lazy(() => import("../../pages/forum/forumPage"));
const ProfilePage = lazy(() => import("../../pages/profile/profilePage"));
const PrevencherPage = lazy(() => import("../../pages/prevencher/prevencherPage"));
const NotFoundPage = lazy(() => import("../../pages/notFound/notFoundPage"));
const SearchPage = lazy(() => import("../../pages/search/SearchPage"));
const GoalsPage = lazy(() => import("../../pages/goals/goalsPage"));
const PaymentSuccessPage = lazy(() => import("../../pages/paymentSuccess/PaymentSuccessPage"));
const PaymentFailedPage = lazy(() => import("../../pages/paymentFailed/PaymentFailedPage"));

function App() {
  const location = useLocation() as TLocationState;
  const navigate = useNavigate();
  const mobile = useMediaQuery(mobileWidth);
  const isAuth = useAppSelector(isAuthSelector);
  const user = useAppSelector(userSelector);
  const chatState = useSelector(chatStateSelector);
  const appointments = useAppSelector(userAppointmentsSelector);

  const hasAccessToken = !!getCookie("accessToken");
  const { isLoading: isUserLoading } = useGetUserQuery(undefined, {
    skip: !hasAccessToken,
  });
  const [getContactDetails] = useLazyGetContactDetailsQuery();
  const { setContactDetails, clearContactSession, logout } = useActions();
  const activeContactToken = useAppSelector(activeContactTokenSelector);
  const [activeCategory, setActiveCategory] = useState(Category.Day);
  const [subCategory, setSubCategory] = useState(subCategories.progress);
  const [showWarning, setShowWarning] = useState(false);

  // The returning-caller questionnaire. Asked once the patient is signed in; the server answers
  // null for any company that has not switched the rule on, which is almost all of them.
  const { data: hotlineQuestionnaire } = useGetMyHotlineQuestionnaireQuery(undefined, {
    skip: !isAuth,
    // Asked again on every mount, like the card in the questionnaires tab. Nothing invalidates
    // this cache -- the answer changes when the patient submits the questionnaire on a page this
    // app never sees -- so a cached "you still owe it" would outlive the fill that settled it and
    // pop the dialog at somebody who had just finished.
    refetchOnMountOrArgChange: true,
  });

  /**
   * Shown once per signed-in session and then not again, dismissed or not.
   *
   * Held in component state rather than storage on purpose: a patient who closes it should not
   * see it again while they are working, and should see it next time they come back. Nothing
   * depends on it being acted on — the card stays in the tab either way.
   */
  const [hotlinePopupSeen, setHotlinePopupSeen] = useState(false);
  const showHotlinePopup =
    isAuth && !hotlinePopupSeen && Boolean(hotlineQuestionnaire?.questionnaire?.canFill);

  const goToQuestionnaires = () => {
    setHotlinePopupSeen(true);
    setActiveCategory(Category.Treatment);
    setSubCategory(subCategories.questionnaires);
    navigate("/treatment");
  };
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  //const [close, setClose] = useState(false);
  // const isSuperAd = useSelector(isSuperAdmin);
  // const [dataFromL, setDataFromL] = useState(false);
  // const [translationMode, setTranslationMode] = useState<boolean>(dataFromL);
  // const dispatch = useDispatch();

  //בעת עלית האתר ריצה על כל המפתחות והוספת אטריביוט
  // useEffect(() => {
  //   if (i18n.isInitialized) {
  //     addKeysToAllTranslatedElements();
  //   } else {
  //     i18n.on("initialized", () => {
  //       addKeysToAllTranslatedElements();
  //     });
  //   }
  // }, [i18n.isInitialized]);
  //
  // useEffect(() => {
  //   setTranslationMode(dataFromL);
  // }, [dataFromL]);
  //
  // useEffect(() => {
  //   const admin = sessionStorage.getItem("isAdmin") === "true";
  //   if (admin && !close) {
  //     dispatch(setSuperAdmin({ flag: true }));
  //     setTranslationMode(true);
  //   } else if (isSuperAd && !close) {
  //     setTranslationMode(true);
  //   }
  // }, [isSuperAd, close, dispatch]);
  useInactivityTimer(setShowWarning, setShowLogoutModal);

  // Check for activeContactToken on app initialization to restore contact session
  useEffect(() => {
    if (activeContactToken && !isUserLoading) {
      // User is authenticated (patient data loaded), now get contact details
      getContactDetails(user?.id ? { patientId: user.id } : undefined)
        .unwrap()
        .then((contactResult: any) => {
          if (contactResult.user && contactResult.linkedClients) {


            setContactDetails(contactResult);
            // Auto-navigate to select-patient if multiple patients and on home page
            const linkedClients = contactResult.linkedClients || [];
            if (linkedClients.length > 1 && !contactResult.activeClient) {
              navigate('/select-patient');
            }
          } else {
            // Contact token invalid - clear session
            clearContactSession();
          }
        })
        .catch(() => {
          // Contact token expired - logout completely
          logout();
        });
    }
  }, [activeContactToken, user.id]);

  //get user appointments
  const [getAppointmentsByUserId] = useLazyGetAppointmentsByUserIdQuery();
  useEffect(() => {
    isAuth&&getAppointmentsByUserId();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);
  //ws
  const [connect] = useLazyInitializeConnectionQuery();
  const [disconnect] = useDisconnectConnectionMutation();
  const existingConnection = useTypedSelector(chatConnectionSelector);
  useEffect(() => {
    // Connect only when authenticated, has user id, and has at least one appointment
    if (isAuth && user?.id && (appointments?.length || 0) > 0) {
      if (!existingConnection) {
        connect({ userId: user.id, shouldConnect: true });
      }
    } else {
      // If auth is lost or no appointments, disconnect
      if (existingConnection) {
        disconnect();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, user?.id, appointments?.length]);

  // disconnect on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeCategory = useCallback((e: React.MouseEvent | null, cat: Category) => {
    if (!mobile) {
      e?.stopPropagation();
    }
    if (location.pathname !== "/home") {
      navigate("/home");
    }
    setActiveCategory(cat);
  }, [mobile, location.pathname, navigate]);


  //redirect from button click
  useEffect(() => {
    if (location.state?.pathname === "/treatment") {
      setSubCategory(subCategories.settings);
    } else if (
      location.state?.pathname === "techSupport" ||
      location.state?.pathname === customerService ||
      location.state?.pathname === "terms"
    ) {
      setSubCategory(subCategories.settings);
    } else if (location.state?.pathname === "profile") {
      setSubCategory(subCategories.account);
    } else if (location.state?.pathname === "notifications") {
      setSubCategory(subCategories.invoices);
    } else if (location.state?.pathname === "updateCard" || location.state?.pathname === "myCard") {
      setSubCategory(subCategories.card);
    } else if (location.state?.pathname === "myCalendar") {
      setSubCategory(subCategories.calendar);
    }
  }, [location.state?.pathname]);

  // const hasOpenedModalRef = useRef(false);
  // const [openModal, setOpenModal] = useState(false);

  // useEffect(() => {
  //   if (!hasOpenedModalRef.current) {
  //     setOpenModal(true);
  //     hasOpenedModalRef.current = true;
  //   } else (window as any).enableTranslationMode?.(translationMode);
  // }, [translationMode]);

  // const handleToggle = () => {
  //   if (translationMode) {
  //     setClose(true);
  //     setTranslationMode(false);
  //   } else {
  //     setClose(false);
  //     setTranslationMode(true);
  //   }
  // };

  // GMT
  const [showGMTPopup, setShowGMTPopup] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("privacyConsent");
    if (!consent) {
      setShowGMTPopup(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem("privacyConsent", "true");
    setShowGMTPopup(false);
  };

  if (isUserLoading) {
    return <Loader />;
  }
  return (
    <>
      <LanguageSelection />
      {/*{isSuperAd && (*/}
      {/*  <ChangeEditingModeButton*/}
      {/*    translationMode={translationMode}*/}
      {/*    onToggle={handleToggle}*/}
      {/*  />*/}
      {/*)}*/}
      {showGMTPopup && (
        <Modal onClose={() => { }}>
          <PrivacyPolicyConsentPopup handleAccept={handleAccept} />
        </Modal>
      )}
      {showHotlinePopup && (
        <Modal onClose={() => setHotlinePopupSeen(true)}>
          <HotlineQuestionnairePopup
            name={hotlineQuestionnaire!.questionnaire!.name}
            onClose={() => setHotlinePopupSeen(true)}
            onGoToQuestionnaires={goToQuestionnaires}
          />
        </Modal>
      )}
      {isAuth && showWarning && (
        <Modal onClose={() => setShowWarning(false)}>
          <InactivityWarning onClose={() => setShowWarning(false)} />
        </Modal>
      )}
      {showLogoutModal && (
        <Modal onClose={() => setShowLogoutModal(false)}>
          <InactivityLogoutPopup onClose={() => setShowLogoutModal(false)} />
        </Modal>
      )}
      {/*{translationMode && <EditModeNotice />}*/}

      {chatState.call.isReceivingCall && <VideoCallWindow />}
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route path={"/"} element={<Onboarding />} />
          <Route
            path="/login/:guid?"
            element={
              <PublicOnlyRoute isAuth={isAuth}>
                <LoginPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/login/enter_code"
            element={
              <PublicOnlyRoute isAuth={isAuth}>
                <LoginSmsCode />
              </PublicOnlyRoute>
            }
          />
          {/* <Route
            path="/signin"
            element={
              <PublicOnlyRoute isAuth={isAuth}>
                <SigninPage />
              </PublicOnlyRoute>
            }
          />
          <Route
            path="/signin/private"
            element={
              <PublicOnlyRoute isAuth={isAuth}>
                <SigninPrivatePage />
              </PublicOnlyRoute>
            }
          /> */}
          <Route
            path={"/restore_password"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <RestorePasswordPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={"/home"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={activeCategory}
                  changeCategory={changeCategory}
                >
                  <HomePage
                    activeCategory={activeCategory}
                    setCategory={setSubCategory}
                  />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/profile"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Profile}
                  changeCategory={changeCategory}
                >
                  <ProfilePage activeCategory={subCategory} />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/treatment"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Treatment}
                  changeCategory={changeCategory}
                >
                  <TreatmentPage
                    setActiveCategory={setActiveCategory}
                    activeCategory={subCategory}
                  />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/treatment/search"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Treatment}
                  changeCategory={changeCategory}
                >
                  <SearchPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/goals"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Goals}
                  changeCategory={changeCategory}
                >
                  <GoalsPage activeCategory={subCategory} />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/prevencher"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Prevencher}
                  changeCategory={changeCategory}
                >
                  <PrevencherPage
                    changeCategory={changeCategory}
                  />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          {/* <Route
            path={"/chat/:guid"}
            element={
              <ChatPage
                activeCategory={Category.Messages}
                changeCategory={changeCategory}
              />
            }
          /> */}
          <Route
            path={"/chat"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <ChatPage
                  activeCategory={Category.Messages}
                  changeCategory={changeCategory}
                />
              </ProtectedRoute>
            }
          />
          <Route path={"/guests_chat"} element={<PocGuestsChatPage />} />
          <Route
            path={"/terms"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Terms}
                  changeCategory={changeCategory}
                >
                  <Terms />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/forum"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Help}
                  changeCategory={changeCategory}
                >
                  <ForumPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={"/forum/:id"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <MainContentLayout
                  activeCategory={Category.Help}
                  changeCategory={changeCategory}
                >
                  <ForumPage />
                </MainContentLayout>
              </ProtectedRoute>
            }
          />
          <Route path={"*"} element={<NotFoundPage />} />
          <Route
            path={"/select-patient"}
            element={
              <ProtectedRoute isAuth={isAuth}>
                <SelectPatientPage />
              </ProtectedRoute>
            }
          />

          <Route path={"/consent-response"} element={<ConsentResponsePage />} />
          <Route path={"/payment-success"} element={<PaymentSuccessPage />} />
          <Route path={"/payment-failed"} element={<PaymentFailedPage />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;

declare global {
  interface Window {
    enableTranslationMode: (isEnabled: boolean) => void;
  }
}
